import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Edit2, Trash2, Upload, Save, Building2, Image as ImageIcon, Shield, CheckCircle, XCircle, Loader2, Eye, EyeOff, KeyRound, Bell, PlayCircle, Link2, Copy, RefreshCw, History, FileText } from 'lucide-react';
import { StateDialog } from '../components/StateDialog';
import { PavilionsCard } from '../components/PavilionsCard';
import { MedilinkMonitorCard } from '../components/MedilinkMonitorCard';
import { ApiKeysCard } from '../components/ApiKeysCard';
import { FechaPagoSyncCard } from '../components/FechaPagoSyncCard';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [states, setStates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [config, setConfig] = useState({
    clinic_name: 'Clínica Agenda',
    clinic_logo: null,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Medilink token state
  const [tokenStatus, setTokenStatus] = useState({ has_token: false, masked_token: '' });
  const [newToken, setNewToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [tokenResult, setTokenResult] = useState(null);

  // Payment log backfill state
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillReport, setBackfillReport] = useState(null);
  const [backfillMode, setBackfillMode] = useState('dry'); // 'dry' | 'apply'
  const [testingConnection, setTestingConnection] = useState(false);

  // Reminder settings
  const [reminderSettings, setReminderSettings] = useState({ enabled: true, offsets_hours: [48, 24], job_interval_minutes: 60 });
  const [offsetsInput, setOffsetsInput] = useState('48, 24');
  const [savingReminders, setSavingReminders] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);
  const [remindersMsg, setRemindersMsg] = useState(null);

  // Share token público del calendario
  const [shareToken, setShareToken] = useState(null);
  const [shareTokenCreatedAt, setShareTokenCreatedAt] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  // Medilink: tipos de documentos clínicos (solo lectura)
  const [docTypesOpen, setDocTypesOpen] = useState(false);
  const [docTypesLoading, setDocTypesLoading] = useState(false);
  const [docTypesError, setDocTypesError] = useState(null);
  const [docTypes, setDocTypes] = useState([]);

  const handleViewDocTypes = async () => {
    setDocTypesOpen(true);
    setDocTypesLoading(true);
    setDocTypesError(null);
    setDocTypes([]);
    try {
      const { data } = await axios.get(`${API_URL}/api/medilink/documentos-clinicos/tipos`, { withCredentials: true });
      setDocTypes(Array.isArray(data?.tipos) ? data.tipos : []);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = detail || 'Error al consultar Medilink';
      if (status === 429) {
        const retry = err.response?.headers?.['retry-after'];
        msg = `Medilink saturado (rate limit). Reintenta en ${retry || 30}s.`;
      }
      setDocTypesError(msg);
    } finally {
      setDocTypesLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchConfig();
    fetchTokenStatus();
    fetchReminderSettings();
    fetchShareToken();
  }, []);

  const fetchShareToken = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/config/calendar-share`, { withCredentials: true });
      setShareToken(data.token);
      setShareTokenCreatedAt(data.created_at);
    } catch (err) {
      console.error(err);
    }
  };

  const regenerateShareToken = async () => {
    if (shareToken && !window.confirm('Se generará un nuevo enlace. El enlace anterior dejará de funcionar. ¿Continuar?')) return;
    setShareLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/config/calendar-share/regenerate`, {}, { withCredentials: true });
      setShareToken(data.token);
      setShareTokenCreatedAt(data.created_at);
    } catch (err) {
      alert('Error al generar el enlace');
    }
    setShareLoading(false);
  };

  const revokeShareToken = async () => {
    if (!window.confirm('¿Revocar el enlace público? Ya nadie podrá acceder al calendario con la URL actual.')) return;
    setShareLoading(true);
    try {
      await axios.delete(`${API_URL}/api/config/calendar-share`, { withCredentials: true });
      setShareToken(null);
      setShareTokenCreatedAt(null);
    } catch (err) {
      alert('Error al revocar el enlace');
    }
    setShareLoading(false);
  };

  const shareUrl = shareToken ? `${window.location.origin}/publico/calendario/${shareToken}` : '';

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    }
  };

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/config`, { withCredentials: true });
      setConfig(data);
      if (data.clinic_logo) setLogoPreview(data.clinic_logo);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/states`, { withCredentials: true });
      setStates(data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchTokenStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/medilink/token-status`, { withCredentials: true });
      setTokenStatus(data);
    } catch (error) {
      console.error('Error fetching token status:', error);
    }
  };

  const handleDelete = async (stateId) => {
    if (!window.confirm('¿Estás seguro de eliminar este estado?')) return;
    try {
      await axios.delete(`${API_URL}/api/states/${stateId}`, { withCredentials: true });
      fetchStates();
    } catch (error) {
      alert('Error al eliminar estado');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('El logo debe ser menor a 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setConfig({ ...config, clinic_logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config`, {
        clinic_name: config.clinic_name,
        clinic_logo: config.clinic_logo,
      }, { withCredentials: true });
      alert('Configuración guardada exitosamente');
    } catch (error) {
      alert('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveToken = async () => {
    if (!newToken.trim()) return;
    setSavingToken(true);
    setTokenResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/medilink/update-token`, {
        token: newToken.trim(),
      }, { withCredentials: true });
      setTokenResult(data);
      if (data.success) {
        setNewToken('');
        fetchTokenStatus();
      }
    } catch (error) {
      setTokenResult({
        success: false,
        message: error.response?.data?.detail || 'Error al guardar token',
      });
    } finally {
      setSavingToken(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTokenResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/medilink/test-connection`, {}, { withCredentials: true });
      setTokenResult({
        success: data.ok,
        message: data.ok ? 'Conexión exitosa con Medilink API v5' : data.error,
        connection_test: data,
      });
    } catch (error) {
      setTokenResult({
        success: false,
        message: error.response?.data?.detail || 'Error al probar conexión',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const fetchReminderSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reminders/settings`, { withCredentials: true });
      setReminderSettings(data);
      setOffsetsInput((data.offsets_hours || []).join(', '));
    } catch (error) {
      console.error('Error fetching reminder settings:', error);
    }
  };

  const handleSaveReminderSettings = async () => {
    setSavingReminders(true);
    setRemindersMsg(null);
    const offsets = offsetsInput
      .split(',')
      .map((x) => parseInt(x.trim(), 10))
      .filter((x) => Number.isFinite(x) && x > 0);
    try {
      const { data } = await axios.put(`${API_URL}/api/reminders/settings`, {
        enabled: reminderSettings.enabled,
        offsets_hours: offsets,
        job_interval_minutes: reminderSettings.job_interval_minutes,
      }, { withCredentials: true });
      setReminderSettings(data);
      setOffsetsInput((data.offsets_hours || []).join(', '));
      setRemindersMsg({ ok: true, text: 'Configuración de recordatorios guardada' });
    } catch (error) {
      setRemindersMsg({ ok: false, text: error.response?.data?.detail || 'Error al guardar' });
    } finally {
      setSavingReminders(false);
    }
  };

  const handleRunRemindersNow = async () => {
    setRunningReminders(true);
    setRemindersMsg(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/reminders/run-now`, {}, { withCredentials: true });
      setRemindersMsg({ ok: true, text: `Se generaron ${data.alerts_created} alerta(s)` });
    } catch (error) {
      setRemindersMsg({ ok: false, text: error.response?.data?.detail || 'Error al ejecutar' });
    } finally {
      setRunningReminders(false);
    }
  };

  const handleBackfillPaymentLog = async (mode) => {
    setBackfillLoading(true);
    setBackfillMode(mode);
    setBackfillReport(null);
    try {
      const dry = mode === 'dry';
      const { data } = await axios.post(
        `${API_URL}/api/management/payment-log/backfill?dry_run=${dry}`,
        {},
        { withCredentials: true }
      );
      setBackfillReport(data);
    } catch (error) {
      setBackfillReport({ error: error.response?.data?.detail || 'Error al reconstruir' });
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1C18] tracking-tight">Configuración</h1>
        <p className="text-[#6A736B] mt-1 text-sm">Personaliza tu clínica y estados del sistema</p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-[#F3EBE1] p-1 h-auto rounded-xl">
          <TabsTrigger value="general" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1C18] rounded-lg px-4 py-2" data-testid="settings-tab-general">
            General
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="api-keys" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1C18] rounded-lg px-4 py-2" data-testid="settings-tab-api-keys">
              API Keys
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="space-y-6">

      {/* Configuración de Clínica */}
      <Card className="bg-white rounded-xl border border-[#E8E5DD]">
        <CardHeader>
          <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
            <Building2 size={24} className="text-[#2B4C3B]" />
            Información de la Clínica
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="clinic_name">Nombre de la Clínica</Label>
            <Input
              id="clinic_name"
              value={config.clinic_name}
              onChange={(e) => setConfig({ ...config, clinic_name: e.target.value })}
              className="mt-1 bg-transparent border-[#E8E5DD]"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <ImageIcon size={18} />
              Logo de la Clínica
            </Label>
            <p className="text-sm text-[#6A736B] mt-1 mb-3">400x400px · PNG, JPG · Máx 2MB</p>
            <div className="flex gap-4 items-start">
              <div className="w-32 h-32 border-2 border-dashed border-[#E8E5DD] rounded-lg flex items-center justify-center bg-[#FDFBF7] overflow-hidden">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="text-center p-4">
                    <ImageIcon className="mx-auto text-[#6A736B] mb-2" size={32} />
                    <p className="text-xs text-[#6A736B]">Sin logo</p>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input type="file" id="clinic_logo" accept="image/png,image/jpeg,image/jpg" onChange={handleLogoChange} className="hidden" />
                <Button type="button" variant="outline" onClick={() => document.getElementById('clinic_logo').click()} className="w-full">
                  <Upload className="mr-2" size={18} /> Subir Logo
                </Button>
                {logoPreview && (
                  <Button type="button" variant="ghost" onClick={() => { setLogoPreview(null); setConfig({ ...config, clinic_logo: null }); }} className="w-full mt-2 text-red-600 hover:text-red-700">
                    Quitar Logo
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="pt-4 border-t border-[#E8E5DD]">
            <Button onClick={handleSaveConfig} disabled={saving} className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white">
              <Save className="mr-2" size={18} />
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Monitor de API Medilink */}
      <MedilinkMonitorCard />

      {/* Sync masivo de fecha_pago histórico (admin) */}
      {isAdmin && <FechaPagoSyncCard />}

      {/* Reconstrucción Histórica de Historial de Abonos */}
      <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="payment-log-backfill-card">
        <CardHeader>
          <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
            <History size={22} className="text-[#8B5CF6]" />
            Reconstrucción de Historial de Abonos
          </CardTitle>
          <p className="text-sm text-[#6A736B] mt-1">
            Rellena retroactivamente el <span className="font-mono">payment_log</span> para cirugías con Monto Abonado &gt; 0 pero sin historial, usando fechas y montos extraídos del registro de auditoría. <span className="font-semibold">100% local — cero consumo Medilink.</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleBackfillPaymentLog('dry')}
              disabled={backfillLoading}
              variant="outline"
              className="border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
              data-testid="backfill-dry-run-btn"
            >
              {backfillLoading && backfillMode === 'dry' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye size={16} className="mr-2" />
              )}
              Simular (dry-run)
            </Button>
            <Button
              onClick={() => {
                if (window.confirm('Se aplicarán los cambios sobre las cirugías detectadas. La operación es idempotente (no toca cirugías que ya tienen historial). ¿Continuar?')) {
                  handleBackfillPaymentLog('apply');
                }
              }}
              disabled={backfillLoading}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              data-testid="backfill-apply-btn"
            >
              {backfillLoading && backfillMode === 'apply' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <History size={16} className="mr-2" />
              )}
              Aplicar reconstrucción
            </Button>
          </div>

          {backfillReport && (
            <div
              className={`rounded-lg border p-4 ${
                backfillReport.error
                  ? 'bg-red-50 border-red-200'
                  : backfillReport.dry_run
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-emerald-50 border-emerald-200'
              }`}
              data-testid="backfill-report"
            >
              {backfillReport.error ? (
                <p className="text-sm text-red-700 font-medium">Error: {backfillReport.error}</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <p><span className="font-semibold">Modo:</span> {backfillReport.dry_run ? 'Simulación' : 'Aplicado'}</p>
                    <p><span className="font-semibold">Escaneadas:</span> {backfillReport.scanned}</p>
                    <p><span className="font-semibold">Reconstruidas:</span> {backfillReport.reconstructed}</p>
                    <p><span className="font-semibold">Entradas creadas:</span> {backfillReport.entries_created}</p>
                    <p><span className="font-semibold">Fallback (sin audit):</span> {backfillReport.fallback_used}</p>
                    <p><span className="font-semibold">Descartadas:</span> {backfillReport.skipped_no_data}</p>
                  </div>
                  {(backfillReport.details || []).length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium text-[#1A1C18]">
                        Ver detalle ({backfillReport.details.length} cirugías)
                      </summary>
                      <div className="mt-2 max-h-80 overflow-auto text-xs border border-[#E8E5DD] rounded bg-white">
                        <table className="w-full">
                          <thead className="bg-[#FDFBF7] sticky top-0">
                            <tr className="text-left">
                              <th className="py-1.5 px-2 font-semibold">Paciente</th>
                              <th className="py-1.5 px-2 font-semibold">Estado</th>
                              <th className="py-1.5 px-2 text-right font-semibold">Abonado</th>
                              <th className="py-1.5 px-2 text-right font-semibold">Suma</th>
                              <th className="py-1.5 px-2 font-semibold">Entradas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {backfillReport.details.map((d) => (
                              <tr key={d.surgery_id} className="border-t border-[#E8E5DD]/60">
                                <td className="py-1.5 px-2">{d.patient_name || d.surgery_id}</td>
                                <td className="py-1.5 px-2">
                                  {d.status === 'reconstructed'
                                    ? (d.fallback_used ? 'Fallback' : 'OK')
                                    : 'Descartada'}
                                </td>
                                <td className="py-1.5 px-2 font-mono text-right">${(d.atencion_abonado || 0).toLocaleString('es-CL')}</td>
                                <td className="py-1.5 px-2 font-mono text-right">
                                  {d.sum_reconstructed != null ? `$${d.sum_reconstructed.toLocaleString('es-CL')}` : '—'}
                                </td>
                                <td className="py-1.5 px-2 text-[#6A736B]">
                                  {(d.entries || []).map((e, i) => (
                                    <div key={i} className="font-mono">{e.fecha}: ${e.monto.toLocaleString('es-CL')}</div>
                                  ))}
                                  {d.reason && <span className="italic">{d.reason}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Medilink Token */}
      <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="medilink-token-card">
        <CardHeader>
          <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
            <Shield size={24} className="text-[#2B4C3B]" />
            Integración Medilink API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Current status */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-[#E8E5DD] bg-[#FDFBF7]">
            {tokenStatus.has_token ? (
              <>
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#1A1C18]">Token configurado</p>
                  <p className="text-xs text-[#6A736B] font-mono mt-0.5">{tokenStatus.masked_token}</p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#1A1C18]">Sin token configurado</p>
                  <p className="text-xs text-[#6A736B]">Ingresa tu token de Medilink para habilitar la integración</p>
                </div>
              </>
            )}
          </div>

          {/* Token input */}
          <div>
            <Label className="flex items-center gap-2">
              <KeyRound size={16} />
              {tokenStatus.has_token ? 'Actualizar Token' : 'Token de Acceso Medilink'}
            </Label>
            <div className="flex gap-2 mt-1.5">
              <div className="relative flex-1">
                <Input
                  data-testid="medilink-token-input"
                  type={showToken ? 'text' : 'password'}
                  value={newToken}
                  onChange={(e) => setNewToken(e.target.value)}
                  placeholder="Pega tu token de Medilink aquí"
                  className="bg-transparent border-[#E8E5DD] pr-10 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6A736B] hover:text-[#1A1C18]"
                >
                  {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <Button
                data-testid="save-token-button"
                onClick={handleSaveToken}
                disabled={savingToken || !newToken.trim()}
                className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
              >
                {savingToken ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                <span className="ml-2">{savingToken ? 'Guardando...' : 'Guardar'}</span>
              </Button>
            </div>
          </div>

          {/* Test connection */}
          {tokenStatus.has_token && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                data-testid="test-connection-button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={testingConnection}
                className="w-full"
              >
                {testingConnection ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield size={16} className="mr-2" />}
                {testingConnection ? 'Probando conexión...' : 'Probar Conexión'}
              </Button>
              <Button
                data-testid="view-doc-types-button"
                variant="outline"
                onClick={handleViewDocTypes}
                className="w-full"
              >
                <FileText size={16} className="mr-2" />
                Ver tipos de documentos clínicos
              </Button>
            </div>
          )}

          {/* Result */}
          {tokenResult && (
            <div className={`p-3 rounded-lg border flex items-start gap-2 ${tokenResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="token-result">
              {tokenResult.success ? (
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-sm ${tokenResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                {tokenResult.message}
              </p>
            </div>
          )}

          <div className="text-xs text-[#6A736B] bg-[#F5F3EE] p-3 rounded-lg">
            <p className="font-medium mb-1">Seguridad del Token</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>El token se almacena encriptado en la base de datos</li>
              <li>Solo el administrador puede ver y modificar el token</li>
              <li>Todos los cambios quedan registrados en el historial de auditoría</li>
              <li>API v5: api.medilink2.healthatom.com</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Link Público del Calendario */}
      <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="share-token-card">
        <CardHeader>
          <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#2B4C3B]" />
            Enlace Público del Calendario
          </CardTitle>
          <p className="text-sm text-[#6A736B] mt-2">
            Comparte el calendario en modo solo lectura (sin RUT ni montos). El enlace incluye un token único que puedes regenerar o revocar.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {shareToken ? (
            <>
              <div className="flex items-start gap-2 bg-[#FDFBF7] border border-[#E8E5DD] rounded-lg p-3">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  data-testid="share-url-input"
                  className="flex-1 bg-transparent text-sm text-[#1A1C18] font-mono outline-none min-w-0 overflow-x-auto"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyShareUrl}
                  data-testid="copy-share-url"
                  className="flex-shrink-0"
                >
                  {copiedShare ? <CheckCircle size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  <span className="ml-1.5">{copiedShare ? '¡Copiado!' : 'Copiar'}</span>
                </Button>
              </div>
              {shareTokenCreatedAt && (
                <p className="text-xs text-[#6A736B]">
                  Creado el {new Date(shareTokenCreatedAt).toLocaleString('es-CL')}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={regenerateShareToken}
                  disabled={shareLoading}
                  data-testid="regenerate-share-token"
                >
                  <RefreshCw size={14} className={`mr-1.5 ${shareLoading ? 'animate-spin' : ''}`} />
                  Regenerar enlace
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={revokeShareToken}
                  disabled={shareLoading}
                  data-testid="revoke-share-token"
                  className="text-red-700 hover:bg-red-50 hover:text-red-800"
                >
                  <XCircle size={14} className="mr-1.5" />
                  Revocar enlace
                </Button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                <strong>💡 Recuerda:</strong> cualquiera con el enlace verá el calendario. Si crees que se filtró, presiona &quot;Regenerar&quot; para invalidarlo.
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-[#6A736B] mb-3">Aún no has generado un enlace público.</p>
              <Button
                onClick={regenerateShareToken}
                disabled={shareLoading}
                data-testid="create-share-token"
                className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
              >
                <Link2 size={14} className="mr-1.5" />
                {shareLoading ? 'Generando...' : 'Generar enlace público'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estados de Cirugía */}
      <Card className="bg-white rounded-xl border border-[#E8E5DD]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-[#1A1C18]">Estados de Cirugía</CardTitle>
          <Button
            data-testid="add-state-button"
            onClick={() => { setSelectedState(null); setDialogOpen(true); }}
            className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white rounded-lg"
          >
            <Plus className="mr-2" size={20} /> Nuevo Estado
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {states.map((state) => (
              <div key={state.id} data-testid={`state-item-${state.name}`} className="flex items-center justify-between p-4 rounded-lg hover:bg-[#FDFBF7] transition-colors border border-[#E8E5DD]">
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: state.color }}></div>
                  <div>
                    <p className="font-medium text-[#1A1C18]">{state.name}</p>
                    <div className="flex gap-2 mt-1">
                      {state.blocks_schedule && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">Bloquea agenda</span>}
                      {state.requires_payment && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Requiere pago</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`edit-state-${state.name}`} variant="ghost" size="sm" onClick={() => { setSelectedState(state); setDialogOpen(true); }} className="text-[#2B4C3B] hover:bg-[#F3EBE1]">
                    <Edit2 size={16} />
                  </Button>
                  <Button data-testid={`delete-state-${state.name}`} variant="ghost" size="sm" onClick={() => handleDelete(state.id)} className="text-[#A65C5C] hover:bg-red-50">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {dialogOpen && (
        <StateDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setSelectedState(null); fetchStates(); }}
          state={selectedState}
        />
      )}

      <PavilionsCard />

      {/* Recordatorios Automáticos */}
      <Card className="bg-white rounded-2xl border border-[#E8E5DD]" data-testid="reminders-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[#1A1C18]">
            <Bell size={20} className="text-[#2B4C3B]" />
            Recordatorios Automáticos
          </CardTitle>
          <p className="text-sm text-[#6A736B] mt-1">
            Envía alertas in-app al doctor y consultora antes de cada cirugía.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="reminders-enabled-toggle">
              <input
                type="checkbox"
                checked={!!reminderSettings.enabled}
                onChange={(e) => setReminderSettings({ ...reminderSettings, enabled: e.target.checked })}
                className="h-4 w-4 accent-[#2B4C3B]"
              />
              <span className="text-sm text-[#1A1C18]">
                {reminderSettings.enabled ? 'Habilitado' : 'Deshabilitado'}
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-[#1A1C18]">Horas antes (separadas por coma)</Label>
              <Input
                data-testid="reminders-offsets-input"
                value={offsetsInput}
                onChange={(e) => setOffsetsInput(e.target.value)}
                placeholder="Ej: 48, 24, 2"
                className="mt-1"
              />
              <p className="text-xs text-[#6A736B] mt-1">
                Ejemplo: <code>48, 24</code> genera alertas 48h y 24h antes.
              </p>
            </div>
            <div>
              <Label className="text-sm text-[#1A1C18]">Intervalo del job (minutos)</Label>
              <Input
                data-testid="reminders-interval-input"
                type="number"
                min={5}
                max={1440}
                value={reminderSettings.job_interval_minutes}
                onChange={(e) => setReminderSettings({
                  ...reminderSettings,
                  job_interval_minutes: parseInt(e.target.value || '60', 10),
                })}
                className="mt-1"
              />
              <p className="text-xs text-[#6A736B] mt-1">
                Frecuencia con la que se revisa la agenda (5–1440 min).
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              data-testid="reminders-save-btn"
              onClick={handleSaveReminderSettings}
              disabled={savingReminders}
              className="bg-[#2B4C3B] hover:bg-[#1F3A2C] text-white"
            >
              {savingReminders ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Guardar
            </Button>
            <Button
              data-testid="reminders-run-btn"
              onClick={handleRunRemindersNow}
              disabled={runningReminders}
              variant="outline"
              className="border-[#2B4C3B] text-[#2B4C3B] hover:bg-[#F3EBE1]"
            >
              {runningReminders ? <Loader2 size={16} className="animate-spin mr-2" /> : <PlayCircle size={16} className="mr-2" />}
              Ejecutar ahora
            </Button>
          </div>

          {remindersMsg && (
            <div
              data-testid="reminders-msg"
              className={`text-sm px-3 py-2 rounded-lg ${remindersMsg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}
            >
              {remindersMsg.text}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="api-keys" className="space-y-6">
            <ApiKeysCard />
          </TabsContent>
        )}
      </Tabs>

      {/* Modal: Tipos de documentos clínicos (Medilink) */}
      <Dialog open={docTypesOpen} onOpenChange={setDocTypesOpen}>
        <DialogContent className="max-w-2xl" data-testid="doc-types-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
              <FileText size={20} className="text-[#2B4C3B]" />
              Tipos de documentos clínicos (Medilink)
            </DialogTitle>
            <DialogDescription>
              Catálogo obtenido de <span className="font-mono">GET /documentosClinicos/tipos</span>. Solo lectura.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-[200px] max-h-[60vh] overflow-y-auto">
            {docTypesLoading && (
              <div className="flex items-center justify-center py-10 text-[#6A736B]">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Consultando Medilink...
              </div>
            )}

            {!docTypesLoading && docTypesError && (
              <div
                data-testid="doc-types-error"
                className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800 flex items-start gap-2"
              >
                <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{docTypesError}</span>
              </div>
            )}

            {!docTypesLoading && !docTypesError && docTypes.length === 0 && (
              <div className="text-sm text-[#6A736B] text-center py-10">
                Medilink no devolvió tipos de documentos clínicos.
              </div>
            )}

            {!docTypesLoading && !docTypesError && docTypes.length > 0 && (
              <Table data-testid="doc-types-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">ID</TableHead>
                    <TableHead>Nombre</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docTypes.map((t, idx) => (
                    <TableRow key={`${t.id ?? 'row'}-${idx}`} data-testid={`doc-type-row-${t.id ?? idx}`}>
                      <TableCell className="font-mono text-sm">{t.id ?? '—'}</TableCell>
                      <TableCell className="text-sm">{t.nombre || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDocTypesOpen(false)}
              data-testid="doc-types-close-btn"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
